import {
  type AppEnv,
  type AppEnvironment,
  isLocalSupabaseUrl,
} from '@/platform/config/env'
import {
  createLazyLearningService,
  createNotConfiguredLearningService,
  createUnavailableLearningService,
  type LearningService,
} from '@/platform/learning/service'
import { createSupabaseLearningService } from '@/platform/learning/supabase-service'
import { type AppSupabaseClient } from '@/platform/supabase/client'

/**
 * Chooses the learning backend for this build (decision D-02,
 * first-learning-journey §6.1). Nothing ever falls back to the MOCK service:
 *
 *   - pilot: no lessons at all, so Child Home shows an honest empty state;
 *   - local / development with `VITE_LEARNING_SERVICE=mock`: the MOCK lesson
 *     service, explicitly chosen for a simulated demonstration;
 *   - local / development otherwise: the real Supabase RPCs, but only when
 *     the Supabase URL is a local stack. Any other URL (for example the remote
 *     KT1 development project) gets a service whose calls fail, so Child Home
 *     shows "could not load" rather than touching a hosted project.
 *
 * The MOCK choice is guarded twice, both allowlists that fail closed:
 *   - build time: `import.meta.env` values are replaced by constants, so the
 *     dynamic import (mock service and fixture) is removed from every build
 *     that did not explicitly select the mock in local/development;
 *   - run time: the validated environment, which keeps the decision testable.
 */

export const MOCK_LEARNING_ENVIRONMENTS: readonly AppEnvironment[] = [
  'local',
  'development',
]

export function isMockLearningEnabled(environment: AppEnvironment): boolean {
  return MOCK_LEARNING_ENVIRONMENTS.includes(environment)
}

export function createLearningService(
  env: AppEnv,
  client: AppSupabaseClient,
): LearningService {
  if (!isMockLearningEnabled(env.VITE_APP_ENV)) return createUnavailableLearningService()

  if (env.VITE_LEARNING_SERVICE === 'mock') {
    const buildAllowsMock =
      (import.meta.env.VITE_APP_ENV === 'local' ||
        import.meta.env.VITE_APP_ENV === 'development') &&
      import.meta.env.VITE_LEARNING_SERVICE === 'mock'
    return buildAllowsMock
      ? createLazyLearningService(async () => {
          const [{ createMockLearningService }, { MOCK_SCHOOL_PLACES_CHOICE_LESSON }] =
            await Promise.all([
              import('@/platform/learning/mock-service'),
              import('@/domains/english/fixtures/mock-school-places-lesson'),
            ])
          return createMockLearningService({
            lessons: [MOCK_SCHOOL_PLACES_CHOICE_LESSON],
            latencyMs: 300,
          })
        })
      : createNotConfiguredLearningService()
  }

  return isLocalSupabaseUrl(env.VITE_SUPABASE_URL)
    ? createSupabaseLearningService(client)
    : createNotConfiguredLearningService()
}
